import React from 'react';
import { subscribe, flush, isOffline } from '../lib/outbox';

/*
  The strip that says the signal has gone and how many numbers are waiting.

  **Built 10 September 2026 for job 25**, to Group H's approved drawings
  `offline-*.png`. Two bars, and they are deliberately not the same bar:

    **Gold while the numbers are held.** Something is not right and you should
    know, but you are not stopped. Keep counting.

    **Green when they land.** It is over, and the bar goes on its own.

  **It sits under the page bar rather than over the work**, so nothing a person
  is reading moves out from under them.

  **The number is the point.** "8 numbers waiting" is a real count of real
  statements, because count entry sends each quantity on its own as it is typed.
  A rep can look at that and know exactly how much is at risk, which is the one
  thing OnHand could never tell them before.

  **The third state is not in the drawings and had to be added**, because the
  drawings assume the phone will hold the numbers. A browser can refuse storage,
  and a rep counting into a queue that will not survive the browser closing is
  in the worst position of the three: working, told it is fine, and about to
  lose it. So that says so, in red, and says what to do about it.
*/

function Cloud({ crossed }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="var(--on-action)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      {crossed
        ? <><path d="M17.5 18H7a4 4 0 0 1-.6-7.96A6 6 0 0 1 17.7 8.2" /><path d="M3 3l18 18" /></>
        : <><path d="M17.5 18H7a4 4 0 0 1-.6-7.96A6 6 0 1 1 17.5 18z" /><path d="M9.5 13.5l2 2 3.5-3.5" /></>}
    </svg>
  );
}

export default function ConnectionBar() {
  const [s, setS] = React.useState({ waiting: 0, durable: true, offline: false });
  /* How many were waiting at the high-water mark, so "All 8 were sent" says
     eight rather than nought. The count itself is zero by the time it is said. */
  const [landed, setLanded] = React.useState(0);
  const peak = React.useRef(0);

  React.useEffect(() => subscribe(next => {
    setS(next);
    if (next.waiting > peak.current) peak.current = next.waiting;
    if (next.waiting === 0 && peak.current > 0) {
      setLanded(peak.current);
      peak.current = 0;
    }
  }), []);

  /* The green bar is an answer, not a state, so it goes on its own. Five
     seconds is the same as the little pop-up messages, which is deliberate:
     one length of time for "this will clear itself" across the whole app. */
  React.useEffect(() => {
    if (!landed) return undefined;
    const t = setTimeout(() => setLanded(0), 5000);
    return () => clearTimeout(t);
  }, [landed]);

  if (s.waiting > 0) {
    return (
      <div className={'conn-bar ' + (s.durable ? 'hold' : 'risk')}>
        <span className="conn-bar-i"><Cloud crossed /></span>
        <div className="conn-bar-words">
          <div className="conn-bar-t">
            {s.durable
              ? 'No signal. Keep counting.'
              : 'No signal, and this phone will not hold your numbers.'}
          </div>
          <div className="conn-bar-s">
            {s.durable
              ? `${s.waiting} ${s.waiting === 1 ? 'number is' : 'numbers are'} waiting on this phone and will be sent when the signal comes back.`
              : `${s.waiting} ${s.waiting === 1 ? 'number is' : 'numbers are'} held in this page only. Closing OnHand loses them. Move to where there is a signal before you close it.`}
          </div>
        </div>
        <span className="conn-bar-n">{s.waiting}</span>
        {/* Not in the drawing, and it earns its place: `navigator.onLine` only
            knows whether the device thinks it is on a network, so a phone on
            hospital wifi with no route out looks online and every write fails.
            Trying is the only honest test, and a rep who has just walked
            outside should not have to wait fifteen seconds for the timer. */}
        {!isOffline() && (
          <button type="button" className="conn-bar-act" onClick={() => flush()}>Try now</button>
        )}
      </div>
    );
  }

  if (landed > 0) {
    return (
      <div className="conn-bar done">
        <span className="conn-bar-i"><Cloud /></span>
        <div className="conn-bar-words">
          <div className="conn-bar-t">Back online.</div>
          <div className="conn-bar-s">All {landed} {landed === 1 ? 'number was' : 'numbers were'} sent.</div>
        </div>
        <span className="conn-bar-n">{landed}</span>
      </div>
    );
  }

  /*
    **Nothing waiting and no signal still gets a bar**, which the drawings do
    not show because they show a rep mid-count. A rep who opens a sheet with no
    signal and has typed nothing yet needs to know before they type, not after.
  */
  if (s.offline) {
    return (
      <div className="conn-bar hold">
        <span className="conn-bar-i"><Cloud crossed /></span>
        <div className="conn-bar-words">
          <div className="conn-bar-t">No signal. Keep counting.</div>
          <div className="conn-bar-s">Your numbers are kept on this phone and sent when the signal comes back.</div>
        </div>
      </div>
    );
  }

  return null;
}
